import React, { useState, useCallback, useMemo } from 'react';
import { Row, Col, Input, Button, Collapse } from 'reactstrap';
import { ModalButtonBuilder, ButtonBuilder, ToastBuilder } from '../../../../../../../../../GlobalComponents/Builders';
import { parseJSONSafe, capitalizeFirstLetter } from '../../../../../../../../../GlobalComponents/Helpers';
import { ChevronDown, ChevronUp, Trash2, Plus, Copy, ArrowUp, ArrowDown } from 'react-feather';

// ─── Action Registry ─────────────────────────────────────────────────────────
// Each entry defines parameter schemas for the builder UI
const ACTION_REGISTRY = {
    // Conditional
    break: {
        label: 'Break', category: 'Conditional',
        description: 'Conditionally stops action processing',
        params: [
            { name: 'operator', type: 'select', required: true, options: ['=', '==', '!=', '>', '>=', '<', '<=', 'in', 'not in'] },
            { name: 'compareValue', type: 'dynamic', required: true, hint: 'Supports {{placeholders}} and arrays (JSON)' },
            { name: 'setValue', type: 'dynamic', hint: 'Value to set if condition is met' },
        ]
    },
    valueCheck: {
        label: 'Value Check', category: 'Conditional',
        description: 'Returns a value based on a comparison',
        params: [
            { name: 'op', type: 'select', required: true, options: ['eq', 'isnull', 'contains', 'in'] },
            { name: 'value', type: 'dynamic', required: true, hint: 'Value to compare/search for' },
            { name: 'return', type: 'dynamic', required: true, hint: 'Value if condition is true' },
            { name: 'default', type: 'dynamic', hint: 'Value if condition is false' },
        ]
    },
    set_conditional_value: {
        label: 'Set Conditional Value', category: 'Conditional',
        description: 'Sets final_value based on a conditional comparison',
        params: [
            { name: 'operator', type: 'select', required: true, options: ['=', '==', '!=', '>', '>=', '<', '<=', 'in', 'not in'] },
            { name: 'compareValue', type: 'dynamic', required: true, hint: 'Supports {{placeholders}}' },
            { name: 'finalValue', type: 'dynamic', required: true, hint: 'Value if condition is true' },
            { name: 'defaultValue', type: 'dynamic', hint: 'Value if condition is false' },
            { name: 'defaultToInitial', type: 'boolean', hint: 'Keep initial value if condition is false' },
        ]
    },

    // String
    append: { label: 'Append', category: 'String', description: 'Appends to final_value', params: [{ name: 'value', type: 'text', hint: 'Literal string to append' }, { name: 'sourceKey', type: 'text', hint: 'Key from resource_data' }] },
    prepend: { label: 'Prepend', category: 'String', description: 'Prepends to final_value', params: [{ name: 'value', type: 'text', hint: 'Literal or separator' }, { name: 'prependKey', type: 'text', hint: 'Key from resource_data to prepend' }, { name: 'sourceKey', type: 'text', hint: 'Key from resource_data to append after value' }] },
    replace: { label: 'Replace', category: 'String', description: 'Replaces substring in final_value', params: [{ name: 'value', type: 'text', required: true, hint: 'Find' }, { name: 'return', type: 'text', required: true, hint: 'Replace with' }] },
    split: { label: 'Split', category: 'String', description: 'Splits final_value by delimiter', params: [{ name: 'value', type: 'text', required: true, hint: 'Delimiter' }, { name: 'return', type: 'number', hint: 'Index to return' }] },
    join: { label: 'Join', category: 'String', description: 'Joins array into string', params: [{ name: 'value', type: 'text', required: true, hint: 'Delimiter' }] },
    maxLength: { label: 'Max Length', category: 'String', description: 'Truncates to max characters', params: [{ name: 'value', type: 'number', required: true, hint: 'Max character length' }] },
    upper: { label: 'Uppercase', category: 'String', description: 'Converts to uppercase', params: [] },
    lower: { label: 'Lowercase', category: 'String', description: 'Converts to lowercase', params: [] },
    regexReplace: { label: 'Regex Replace', category: 'String', description: 'Regex replace on destination_value', params: [{ name: 'pattern', type: 'text', hint: 'Regex pattern' }, { name: 'type', type: 'select', options: ['', 'nonAlphanumeric'], hint: 'Predefined type' }, { name: 'value', type: 'text', required: true, hint: 'Replacement' }] },

    // Date
    convertDate: { label: 'Convert Date', category: 'Date', description: 'Converts date between formats', params: [{ name: 'value', type: 'text', required: true, hint: 'Input format (e.g. %Y-%m-%d)' }, { name: 'return', type: 'text', required: true, hint: 'Output format (e.g. %m/%d/%Y)' }] },
    smartConvertDate: { label: 'Smart Convert Date', category: 'Date', description: 'Auto-parses any date format', params: [{ name: 'return', type: 'text', required: true, hint: 'Output format' }] },
    get_state_code: { label: 'Get State Code', category: 'Date', description: 'US state name → 2-letter code', params: [] },

    // API Lookup
    destination_lookup: {
        label: 'Destination Lookup', category: 'API Lookup',
        description: 'Queries destination API for matching records',
        params: [
            { name: 'resource', type: 'text', required: true, hint: 'API endpoint (supports {{action_value}})' },
            { name: 'conditions', type: 'text', hint: 'Query filters (supports {{action_value}})' },
            { name: 'fields', type: 'array', hint: 'Fields to return' },
            { name: 'return', type: 'text', hint: 'Key to extract from results' },
            { name: 'returnAll', type: 'boolean', hint: 'Return all matches as array' },
        ]
    },
    destination_api_lookup: {
        label: 'Destination API Lookup', category: 'API Lookup',
        description: 'Advanced destination lookup with dynamic conditions',
        params: [
            { name: 'resource', type: 'text', required: true, hint: 'API endpoint' },
            { name: 'functionKey', type: 'text', hint: 'Single key to search by' },
            { name: 'functionKeys', type: 'array', hint: 'Multiple search keys' },
            { name: 'conditions', type: 'text', hint: 'Additional conditions (supports placeholders)' },
            { name: 'return', type: 'text', hint: 'Key to extract (default: id)' },
        ]
    },
    destination_api_update: {
        label: 'Destination API Update', category: 'API Lookup',
        description: 'Performs API update to destination',
        params: [
            { name: 'resource', type: 'text', required: true, hint: 'API endpoint (supports placeholders)' },
            { name: 'method', type: 'select', required: true, options: ['post', 'patch', 'put'] },
            { name: 'payload', type: 'json', hint: 'Request body (JSON object)' },
            { name: 'payloadKey', type: 'text', hint: 'Fetch payload from data source path' },
            { name: 'return', type: 'text', hint: 'Key to extract from response' },
            { name: 'identifier', type: 'text', hint: 'Unique ID for duplicate prevention' },
            { name: 'settings', type: 'multi-select', options: ['allowDuplicates', 'allowErrors'] },
        ]
    },
    source_api_lookup: {
        label: 'Source API Lookup', category: 'API Lookup',
        description: 'Queries source API for a matching record',
        params: [
            { name: 'resource', type: 'text', required: true, hint: 'API endpoint (supports {{action_value}})' },
            { name: 'conditions', type: 'text', hint: 'Query filters' },
            { name: 'return', type: 'text', required: true, hint: 'Key(s) to extract' },
        ]
    },

    // DB Lookup
    source_db_resource_lookup: {
        label: 'Source DB Resource Lookup', category: 'DB Lookup',
        description: 'Looks up a staged record from the migration DB',
        params: [
            { name: 'resource_id', type: 'number', required: true, hint: 'Resource ID' },
            { name: 'search', type: 'text', hint: 'Column to search (default: rec_id)' },
            { name: 'return', type: 'text', required: true, hint: 'Key to extract' },
            { name: 'persistent_data_only', type: 'boolean', hint: 'Store in persistent_data only' },
        ]
    },
    staged_db_record_reference: {
        label: 'Staged DB Record Reference', category: 'DB Lookup',
        description: 'Queries staged records with custom filters',
        params: [
            { name: 'resource_id', type: 'number', hint: 'Resource ID' },
            { name: 'job_resource_id', type: 'text', hint: 'Job resource ID' },
            { name: 'filters', type: 'json', required: true, hint: 'Array of filter objects' },
            { name: 'returnFields', type: 'json', required: true, hint: 'Array of return field objects' },
        ]
    },
    supplementary_resource_value: {
        label: 'Supplementary Resource Value', category: 'DB Lookup',
        description: 'Looks up value from supplementary resource table',
        params: [{ name: 'resource_id', type: 'number', required: true, hint: 'Resource ID' }, { name: 'returnAll', type: 'boolean' }]
    },

    // Data
    load_data: { label: 'Load Data', category: 'Data', description: 'Loads a JSON data object', params: [{ name: 'data', type: 'json', required: true, hint: 'JSON lookup data' }, { name: 'saveKey', type: 'text', hint: 'Save to persistent_data key' }, { name: 'dataIsFinalValue', type: 'boolean', hint: 'Set entire data as final_value' }] },
    validate: { label: 'Validate', category: 'Data', description: 'Stops if final_value is empty/null', params: [] },
    end_processing: { label: 'End Processing', category: 'Data', description: 'Converts truthy values to boolean True', params: [] },
    persistent_data: { label: 'Persistent Data', category: 'Data', description: 'Retrieves from persistent_data cache', params: [{ name: 'keys', type: 'array', required: true, hint: 'Keys to traverse (max 2 levels)' }] },
    key_mapping_result: { label: 'Key Mapping Result', category: 'Data', description: 'Retrieves from key_mapping_result', params: [{ name: 'keyName', type: 'text', required: true, hint: 'Key path (supports nested /)' }] },
    update_key_mapping_result: { label: 'Update Key Mapping Result', category: 'Data', description: 'Updates key_mapping_result at a key path', params: [{ name: 'keyName', type: 'text', required: true, hint: 'Key path' }, { name: 'keyValue', type: 'dynamic', hint: 'Value (defaults to final_value)' }] },
    update_resource_data: { label: 'Update Resource Data', category: 'Data', description: 'Updates a key in resource_data', params: [{ name: 'keyName', type: 'text', required: true, hint: 'Key to update' }, { name: 'keyValue', type: 'dynamic', required: true, hint: 'Value to set' }] },

    // Array/Dict
    filter_list: { label: 'Filter List', category: 'Array', description: 'Filters list by matching a nested key', params: [{ name: 'filterKey', type: 'text', required: true, hint: 'Nested key path (supports /)' }, { name: 'filterValue', type: 'dynamic', required: true, hint: 'Value to match' }, { name: 'returnFirst', type: 'boolean', hint: 'Return only first match' }, { name: 'returnKey', type: 'text', hint: 'Extract this key from results' }] },
    flatten_list: { label: 'Flatten List', category: 'Array', description: 'Extracts values from a key in each dict', params: [{ name: 'keyName', type: 'text', required: true }] },
    flatten_nested_lists: { label: 'Flatten Nested Lists', category: 'Array', description: 'Flattens nested lists within array of objects', params: [{ name: 'keyName', type: 'text', required: true }] },
    dict_key: { label: 'Dict Key', category: 'Array', description: 'Extracts value from dict by key', params: [{ name: 'keyName', type: 'text', required: true }] },

    // Notes
    create_note: {
        label: 'Create Note', category: 'Notes',
        description: 'Creates a note on source or destination record',
        params: [
            { name: 'target', type: 'select', required: true, options: ['self', 'source', 'destination'] },
            { name: 'note', type: 'textarea', hint: 'Note text (supports placeholders)' },
            { name: 'comment', type: 'textarea', hint: 'Alias for note' },
            { name: 'identifier', type: 'text', hint: 'Tracking ID for duplicate prevention' },
            { name: 'settings', type: 'multi-select', options: ['preventDuplicates'] },
        ]
    },

    // HTML
    extract_html_hyperlinks: { label: 'Extract HTML Hyperlinks', category: 'HTML', description: 'Extracts <a href> links from HTML', params: [] },
    extract_html_images: { label: 'Extract HTML Images', category: 'HTML', description: 'Extracts image URLs from <img> tags', params: [] },

    // Custom
    'custom-medicusit-wellstreet': {
        label: 'Custom: MedicusIT Wellstreet', category: 'Custom',
        description: 'Dispatches to custom MedicusIT Wellstreet functions',
        params: [
            { name: 'function', type: 'select', required: true, options: ['cwm_create_sync_hold_note', 'fs_create_sync_hold_note'], hint: 'Function to dispatch' },
            { name: 'params', type: 'json', hint: 'Function-specific parameters (JSON object)' },
        ]
    },
    'custom-fivenines': { label: 'Custom: FiveNines', category: 'Custom', description: 'Delegates to custom/fivenines.py', params: [] },
    'custom-medicusit': { label: 'Custom: MedicusIT', category: 'Custom', description: 'Delegates to custom/medicusit.py', params: [] },

    // Post-Process
    cache_record_state: {
        label: 'Cache Record State', category: 'Post-Process',
        description: 'Tracks field-level changes across sync runs',
        params: [{ name: 'fields', type: 'array', required: true, hint: 'Field paths to track (use / for nested)' }]
    },

    // Source-Specific
    get_initial_description: { label: 'Get Initial Description', category: 'Source-Specific', description: 'Fetches initial description from CW ticket', params: [] },
    get_cw_primary_contact: { label: 'Get CW Primary Contact', category: 'Source-Specific', description: 'Fetches primary contact for CW company', params: [] },
    find_matching_contact: { label: 'Find Matching Contact', category: 'Source-Specific', description: 'Searches destination for matching contact', params: [] },
};

// Common modifiers available on all actions
const COMMON_MODIFIERS = [
    { name: 'sourceKey', type: 'text', hint: 'Override source field for this action\'s input' },
    { name: 'persistentDataKey', type: 'text', hint: 'Store result in persistent_data with this key' },
    { name: 'persistentDataOnly', type: 'boolean', hint: 'Store only, set final_value to None' },
    { name: 'returnKey', type: 'text', hint: 'Extract a specific key from the result' },
    { name: 'returnKeys', type: 'array', hint: 'Extract specific keys from the result' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const groupedActionOptions = () => {
    const groups = {};
    Object.entries(ACTION_REGISTRY).forEach(([key, val]) => {
        if (!groups[val.category]) groups[val.category] = [];
        groups[val.category].push({ label: val.label, value: key });
    });
    return groups;
};

const parseSmartValue = (val) => {
    if (val === '') return undefined;
    if (val === 'true') return true;
    if (val === 'false') return false;
    if (val === 'null') return null;
    if (val !== '' && !isNaN(val) && !isNaN(parseFloat(val)) && !/^0\d/.test(val)) return Number(val);
    // Try JSON parse for arrays/objects
    try { const p = JSON.parse(val); if (typeof p === 'object') return p; } catch {}
    return val;
};

const displayValue = (val) => {
    if (val === null) return 'null';
    if (val === undefined) return '';
    if (typeof val === 'boolean') return String(val);
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
};

// ─── Parameter Field Renderer ────────────────────────────────────────────────

const ParamField = ({ param, value, onChange, readOnly }) => {
    const { name, type, options, hint, required } = param;
    const labelStyle = { fontSize: 12, fontWeight: 500, marginBottom: 2, color: '#555' };
    const hintStyle = { fontSize: 11, color: '#888', marginTop: 1 };

    switch (type) {
        case 'select':
            return (
                <div className='mb-2'>
                    <label style={labelStyle}>{name}{required && <span className='text-danger'> *</span>}</label>
                    <Input bsSize='sm' type='select' value={value ?? ''} disabled={readOnly}
                        onChange={(e) => onChange(name, e.target.value || undefined)}>
                        <option value=''>Select...</option>
                        {(options || []).filter(Boolean).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </Input>
                    {hint && <div style={hintStyle}>{hint}</div>}
                </div>
            );

        case 'multi-select':
            return (
                <div className='mb-2'>
                    <label style={labelStyle}>{name}{required && <span className='text-danger'> *</span>}</label>
                    <div className='d-flex flex-wrap gap-1'>
                        {(options || []).map(opt => {
                            const selected = Array.isArray(value) && value.includes(opt);
                            return (
                                <Button key={opt} size='sm' outline={!selected} color={selected ? 'primary' : 'secondary'}
                                    disabled={readOnly}
                                    onClick={() => {
                                        const current = Array.isArray(value) ? value : [];
                                        onChange(name, selected ? current.filter(v => v !== opt) : [...current, opt]);
                                    }}
                                    style={{ fontSize: 11, padding: '2px 8px' }}
                                >{opt}</Button>
                            );
                        })}
                    </div>
                    {hint && <div style={hintStyle}>{hint}</div>}
                </div>
            );

        case 'boolean':
            return (
                <div className='mb-2'>
                    <label style={labelStyle}>{name}</label>
                    <Input bsSize='sm' type='select' value={value === true ? 'true' : value === false ? 'false' : ''} disabled={readOnly}
                        onChange={(e) => onChange(name, e.target.value === 'true' ? true : e.target.value === 'false' ? false : undefined)}>
                        <option value=''>Not set</option>
                        <option value='true'>True</option>
                        <option value='false'>False</option>
                    </Input>
                    {hint && <div style={hintStyle}>{hint}</div>}
                </div>
            );

        case 'number':
            return (
                <div className='mb-2'>
                    <label style={labelStyle}>{name}{required && <span className='text-danger'> *</span>}</label>
                    <Input bsSize='sm' type='number' value={value ?? ''} readOnly={readOnly}
                        onChange={(e) => onChange(name, e.target.value === '' ? undefined : Number(e.target.value))} />
                    {hint && <div style={hintStyle}>{hint}</div>}
                </div>
            );

        case 'textarea':
            return (
                <div className='mb-2'>
                    <label style={labelStyle}>{name}{required && <span className='text-danger'> *</span>}</label>
                    <Input bsSize='sm' type='textarea' value={value ?? ''} readOnly={readOnly}
                        style={{ minHeight: 60, fontFamily: 'monospace', fontSize: 12 }}
                        onChange={(e) => onChange(name, e.target.value || undefined)} />
                    {hint && <div style={hintStyle}>{hint}</div>}
                </div>
            );

        case 'json':
            return (
                <div className='mb-2'>
                    <label style={labelStyle}>{name}{required && <span className='text-danger'> *</span>}</label>
                    <Input bsSize='sm' type='textarea' readOnly={readOnly}
                        value={typeof value === 'object' && value !== null ? JSON.stringify(value, null, 2) : (value ?? '')}
                        style={{ minHeight: 80, fontFamily: 'monospace', fontSize: 11 }}
                        onChange={(e) => {
                            const raw = e.target.value;
                            try { onChange(name, JSON.parse(raw)); } catch { onChange(name, raw); }
                        }} />
                    {hint && <div style={hintStyle}>{hint}</div>}
                </div>
            );

        case 'array':
            return (
                <ArrayField name={name} value={value} onChange={onChange} hint={hint} required={required} readOnly={readOnly} />
            );

        case 'dynamic':
            // Smart field that accepts string, number, bool, array, or JSON
            return (
                <div className='mb-2'>
                    <label style={labelStyle}>{name}{required && <span className='text-danger'> *</span>}</label>
                    <Input bsSize='sm' type='text' value={displayValue(value)} readOnly={readOnly}
                        style={{ fontFamily: 'monospace', fontSize: 12 }}
                        onChange={(e) => onChange(name, parseSmartValue(e.target.value))} />
                    {hint && <div style={hintStyle}>{hint}</div>}
                    {value !== undefined && typeof value !== 'string' && <div style={{ fontSize: 10, color: '#6c757d', marginTop: 1 }}>Type: {value === null ? 'null' : Array.isArray(value) ? 'array' : typeof value}</div>}
                </div>
            );

        case 'text':
        default:
            return (
                <div className='mb-2'>
                    <label style={labelStyle}>{name}{required && <span className='text-danger'> *</span>}</label>
                    <Input bsSize='sm' type='text' value={value ?? ''} readOnly={readOnly}
                        onChange={(e) => onChange(name, e.target.value || undefined)} />
                    {hint && <div style={hintStyle}>{hint}</div>}
                </div>
            );
    }
};

// Array field: simple string/value list with add/remove
const ArrayField = ({ name, value, onChange, hint, required, readOnly }) => {
    const items = Array.isArray(value) ? value : [];

    const addItem = () => onChange(name, [...items, '']);
    const removeItem = (idx) => onChange(name, items.filter((_, i) => i !== idx));
    const updateItem = (idx, val) => onChange(name, items.map((item, i) => i === idx ? parseSmartValue(val) : item));

    return (
        <div className='mb-2'>
            <label style={{ fontSize: 12, fontWeight: 500, marginBottom: 2, color: '#555' }}>
                {name}{required && <span className='text-danger'> *</span>}
            </label>
            {items.map((item, idx) => (
                <div key={idx} className='d-flex align-items-center gap-1 mb-1'>
                    <Input bsSize='sm' type='text' value={displayValue(item)} readOnly={readOnly}
                        style={{ fontFamily: 'monospace', fontSize: 12 }}
                        onChange={(e) => updateItem(idx, e.target.value)} />
                    {!readOnly && <Button size='sm' color='link' className='text-danger p-0' style={{ minWidth: 20 }}
                        onClick={() => removeItem(idx)}>✕</Button>}
                </div>
            ))}
            {!readOnly && <Button size='sm' color='link' className='p-0' style={{ fontSize: 11 }} onClick={addItem}>+ Add item</Button>}
            {hint && <div style={{ fontSize: 11, color: '#888', marginTop: 1 }}>{hint}</div>}
        </div>
    );
};

// ─── Single Action Card ──────────────────────────────────────────────────────

const ActionCard = ({ action, index, total, onUpdate, onRemove, onMove, onDuplicate, readOnly, isExpanded, onToggle }) => {
    const [showModifiers, setShowModifiers] = useState(false);
    const registry = ACTION_REGISTRY[action.name];
    const actionGroups = useMemo(() => groupedActionOptions(), []);

    const handleParamChange = (paramName, value) => {
        const updated = { ...action };
        if (value === undefined || value === '') {
            delete updated[paramName];
        } else {
            updated[paramName] = value;
        }
        onUpdate(index, updated);
    };

    const handleNameChange = (newName) => {
        // Keep only 'name' and common modifiers when switching action type
        const preserved = { name: newName };
        COMMON_MODIFIERS.forEach(mod => {
            if (action[mod.name] !== undefined) preserved[mod.name] = action[mod.name];
        });
        onUpdate(index, preserved);
    };

    const actionLabel = registry?.label || action.name || '(unnamed)';
    const actionDesc = registry?.description || '';
    const hasModifiers = COMMON_MODIFIERS.some(mod => action[mod.name] !== undefined);

    // Count filled params for collapsed summary
    const filledParams = registry?.params?.filter(p => action[p.name] !== undefined).length || 0;

    return (
        <div style={{ border: '1px solid #dee2e6', borderRadius: 6, marginBottom: 8, backgroundColor: isExpanded ? '#fff' : '#f8f9fa' }}>
            {/* Header */}
            <div className='d-flex align-items-center justify-content-between px-2 py-1'
                style={{ cursor: 'pointer', borderBottom: isExpanded ? '1px solid #dee2e6' : 'none' }}
                onClick={onToggle}>
                <div className='d-flex align-items-center gap-2'>
                    <span style={{ fontSize: 11, color: '#6c757d', fontWeight: 600, minWidth: 20 }}>#{index + 1}</span>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{actionLabel}</span>
                    {!isExpanded && filledParams > 0 && <span style={{ fontSize: 11, color: '#6c757d' }}>({filledParams} param{filledParams !== 1 ? 's' : ''})</span>}
                    {hasModifiers && <span style={{ fontSize: 10, background: '#e9ecef', borderRadius: 3, padding: '1px 4px', color: '#495057' }}>MOD</span>}
                </div>
                <div className='d-flex align-items-center gap-1'>
                    {!readOnly && <>
                        <Button size='sm' color='link' className='p-0 text-muted' title='Move up' disabled={index === 0}
                            onClick={(e) => { e.stopPropagation(); onMove(index, index - 1); }}><ArrowUp size={13} /></Button>
                        <Button size='sm' color='link' className='p-0 text-muted' title='Move down' disabled={index === total - 1}
                            onClick={(e) => { e.stopPropagation(); onMove(index, index + 1); }}><ArrowDown size={13} /></Button>
                        <Button size='sm' color='link' className='p-0 text-muted' title='Duplicate'
                            onClick={(e) => { e.stopPropagation(); onDuplicate(index); }}><Copy size={13} /></Button>
                        <Button size='sm' color='link' className='p-0 text-danger' title='Remove'
                            onClick={(e) => { e.stopPropagation(); onRemove(index); }}><Trash2 size={13} /></Button>
                    </>}
                    {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </div>
            </div>

            {/* Body */}
            <Collapse isOpen={isExpanded}>
                <div className='px-3 py-2'>
                    {/* Action Name */}
                    <div className='mb-2'>
                        <label style={{ fontSize: 12, fontWeight: 500, color: '#555' }}>Action Name <span className='text-danger'>*</span></label>
                        <Input bsSize='sm' type='select' value={action.name || ''} disabled={readOnly}
                            onChange={(e) => handleNameChange(e.target.value)}>
                            <option value=''>Select action...</option>
                            {Object.entries(actionGroups).map(([category, opts]) => (
                                <optgroup key={category} label={category}>
                                    {opts.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                </optgroup>
                            ))}
                        </Input>
                        {actionDesc && <div style={{ fontSize: 11, color: '#6c757d', marginTop: 2 }}>{actionDesc}</div>}
                    </div>

                    {/* Parameters */}
                    {registry && registry.params.length > 0 && (
                        <div style={{ borderLeft: '3px solid #0d6efd', paddingLeft: 12, marginTop: 8 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: '#0d6efd', marginBottom: 4 }}>Parameters</div>
                            {registry.params.map(param => (
                                <ParamField key={param.name} param={param} value={action[param.name]} onChange={handleParamChange} readOnly={readOnly} />
                            ))}
                        </div>
                    )}

                    {/* Unknown params (not in registry) */}
                    {registry && (() => {
                        const knownKeys = new Set(['name', ...registry.params.map(p => p.name), ...COMMON_MODIFIERS.map(m => m.name)]);
                        const unknownParams = Object.entries(action).filter(([k]) => !knownKeys.has(k));
                        if (unknownParams.length === 0) return null;
                        return (
                            <div style={{ borderLeft: '3px solid #ffc107', paddingLeft: 12, marginTop: 8 }}>
                                <div style={{ fontSize: 11, fontWeight: 600, color: '#856404', marginBottom: 4 }}>Extra Properties</div>
                                {unknownParams.map(([key, val]) => (
                                    <div key={key} className='mb-2'>
                                        <label style={{ fontSize: 12, fontWeight: 500, color: '#555' }}>{key}</label>
                                        <Input bsSize='sm' type='text' readOnly={readOnly}
                                            style={{ fontFamily: 'monospace', fontSize: 12 }}
                                            value={displayValue(val)}
                                            onChange={(e) => handleParamChange(key, parseSmartValue(e.target.value))} />
                                    </div>
                                ))}
                            </div>
                        );
                    })()}

                    {/* No registry — free-form action */}
                    {!registry && action.name && (
                        <div style={{ borderLeft: '3px solid #ffc107', paddingLeft: 12, marginTop: 8 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: '#856404', marginBottom: 4 }}>Unknown Action — Free-form Properties</div>
                            {Object.entries(action).filter(([k]) => k !== 'name' && !COMMON_MODIFIERS.map(m => m.name).includes(k)).map(([key, val]) => (
                                <div key={key} className='mb-2 d-flex align-items-start gap-1'>
                                    <Input bsSize='sm' style={{ width: 120, fontWeight: 500 }} value={key} readOnly={readOnly}
                                        onChange={(e) => {
                                            const updated = { ...action };
                                            delete updated[key];
                                            if (e.target.value) updated[e.target.value] = val;
                                            onUpdate(index, updated);
                                        }} />
                                    <Input bsSize='sm' style={{ fontFamily: 'monospace', fontSize: 12 }} value={displayValue(val)} readOnly={readOnly}
                                        onChange={(e) => handleParamChange(key, parseSmartValue(e.target.value))} />
                                    {!readOnly && <Button size='sm' color='link' className='text-danger p-0' onClick={() => {
                                        const updated = { ...action }; delete updated[key]; onUpdate(index, updated);
                                    }}>✕</Button>}
                                </div>
                            ))}
                            {!readOnly && <Button size='sm' color='link' className='p-0' style={{ fontSize: 11 }}
                                onClick={() => {
                                    const key = `param${Object.keys(action).length}`;
                                    handleParamChange(key, '');
                                }}>+ Add property</Button>}
                        </div>
                    )}

                    {/* Common Modifiers */}
                    <div style={{ marginTop: 10 }}>
                        <Button size='sm' color='link' className='p-0' style={{ fontSize: 11, color: '#6c757d' }}
                            onClick={() => setShowModifiers(!showModifiers)}>
                            {showModifiers ? '▾' : '▸'} Common Modifiers {hasModifiers && '(active)'}
                        </Button>
                        <Collapse isOpen={showModifiers}>
                            <div style={{ borderLeft: '3px solid #6c757d', paddingLeft: 12, marginTop: 4 }}>
                                {COMMON_MODIFIERS.map(mod => (
                                    <ParamField key={mod.name} param={mod} value={action[mod.name]} onChange={handleParamChange} readOnly={readOnly} />
                                ))}
                            </div>
                        </Collapse>
                    </div>
                </div>
            </Collapse>
        </div>
    );
};

// ─── Main ActionsBuilder Component ───────────────────────────────────────────

const ActionsBuilder = ({ getCurrentConditions, onApply, readOnly }) => {
    const [actions, setActions] = useState([]);
    const [expandedIndex, setExpandedIndex] = useState(null);
    const [jsonMode, setJsonMode] = useState(false);
    const [rawJson, setRawJson] = useState('');

    const loadActions = useCallback(() => {
        const raw = typeof getCurrentConditions === 'function' ? getCurrentConditions() : getCurrentConditions;
        const cleaned = typeof raw === 'string' ? raw.replace(/^#/, '').trim() : raw;
        const parsed = parseJSONSafe(cleaned);
        const loadedActions = parsed?.actions || [];
        setActions(loadedActions);
        setRawJson(JSON.stringify({ actions: loadedActions }, null, 2));
        setExpandedIndex(loadedActions.length > 0 ? 0 : null);
        setJsonMode(false);
    }, [getCurrentConditions]);

    const addAction = (name = '') => {
        const newActions = [...actions, { name }];
        setActions(newActions);
        setExpandedIndex(newActions.length - 1);
    };

    const updateAction = (index, updated) => {
        setActions(prev => prev.map((a, i) => i === index ? updated : a));
    };

    const removeAction = (index) => {
        setActions(prev => prev.filter((_, i) => i !== index));
        if (expandedIndex === index) setExpandedIndex(null);
        else if (expandedIndex > index) setExpandedIndex(expandedIndex - 1);
    };

    const moveAction = (from, to) => {
        setActions(prev => {
            const copy = [...prev];
            const [moved] = copy.splice(from, 1);
            copy.splice(to, 0, moved);
            return copy;
        });
        setExpandedIndex(to);
    };

    const duplicateAction = (index) => {
        const copy = JSON.parse(JSON.stringify(actions[index]));
        const newActions = [...actions];
        newActions.splice(index + 1, 0, copy);
        setActions(newActions);
        setExpandedIndex(index + 1);
    };

    const handleApply = () => {
        let finalActions = actions;

        // If in JSON mode, parse from raw
        if (jsonMode) {
            try {
                const parsed = JSON.parse(rawJson);
                finalActions = parsed?.actions || parsed;
                if (!Array.isArray(finalActions)) {
                    ToastBuilder('error', 'JSON must contain an "actions" array');
                    return;
                }
            } catch {
                ToastBuilder('error', 'Invalid JSON');
                return;
            }
        }

        // Validate: each action must have a name
        const invalid = finalActions.findIndex(a => !a.name);
        if (invalid >= 0) {
            ToastBuilder('warn', `Action #${invalid + 1} has no name`);
            return;
        }

        // Clean undefined values
        const cleaned = finalActions.map(action => {
            const obj = {};
            Object.entries(action).forEach(([k, v]) => {
                if (v !== undefined && v !== '') obj[k] = v;
            });
            return obj;
        });

        onApply?.({ actions: cleaned });
        ToastBuilder('info', "Don't forget to Submit to save", {}, 5000);
    };

    const syncRawJson = () => {
        setRawJson(JSON.stringify({ actions }, null, 2));
    };

    const syncFromRawJson = () => {
        try {
            const parsed = JSON.parse(rawJson);
            const parsedActions = parsed?.actions || parsed;
            if (Array.isArray(parsedActions)) {
                setActions(parsedActions);
                setJsonMode(false);
                ToastBuilder('success', 'Loaded from JSON');
            } else {
                ToastBuilder('error', 'Must be an array or {actions: [...]}');
            }
        } catch {
            ToastBuilder('error', 'Invalid JSON');
        }
    };

    return (
        <ModalButtonBuilder
            title='Actions Builder'
            type='button'
            className='btn-warning btn-sm'
            modalTitle='Actions Builder'
            formId='actions-builder-form'
            onSubmit={readOnly ? false : 'Apply Actions'}
            onCancelLabel={readOnly ? 'Close' : 'Cancel'}
            onClick={loadActions}
            style={{ minWidth: 620, maxWidth: 700, width: 680 }}
            body={
                <form id='actions-builder-form' onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); handleApply(); }}>
                    {/* Toolbar */}
                    <div className='d-flex justify-content-between align-items-center mb-2'>
                        <div className='d-flex gap-1 align-items-center'>
                            <span style={{ fontSize: 13, fontWeight: 600 }}>{actions.length} action{actions.length !== 1 ? 's' : ''}</span>
                            {!readOnly && (
                                <Button size='sm' color='link' style={{ fontSize: 11 }} type='button'
                                    onClick={() => { if (jsonMode) syncFromRawJson(); else syncRawJson(); setJsonMode(!jsonMode); }}>
                                    {jsonMode ? '← Visual Editor' : 'JSON Editor →'}
                                </Button>
                            )}
                        </div>
                        {!readOnly && !jsonMode && (
                            <ButtonBuilder label='+ Add Action' type='button' className='btn-outline-primary btn-sm' useSpinner={false} onClick={() => addAction('')} />
                        )}
                    </div>

                    {/* JSON Mode */}
                    {jsonMode ? (
                        <div>
                            <Input type='textarea' value={rawJson} readOnly={readOnly}
                                style={{ minHeight: 400, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre' }}
                                onChange={(e) => setRawJson(e.target.value)} />
                        </div>
                    ) : (
                        /* Visual Mode */
                        <div style={{ maxHeight: 500, overflowY: 'auto', padding: '4px 0' }}>
                            {actions.length === 0 && (
                                <div className='text-center text-muted py-4' style={{ fontSize: 13 }}>
                                    No actions configured. Click "+ Add Action" to begin.
                                </div>
                            )}
                            {actions.map((action, idx) => (
                                <ActionCard
                                    key={idx}
                                    action={action}
                                    index={idx}
                                    total={actions.length}
                                    onUpdate={updateAction}
                                    onRemove={removeAction}
                                    onMove={moveAction}
                                    onDuplicate={duplicateAction}
                                    readOnly={readOnly}
                                    isExpanded={expandedIndex === idx}
                                    onToggle={() => setExpandedIndex(expandedIndex === idx ? null : idx)}
                                />
                            ))}
                        </div>
                    )}
                </form>
            }
        />
    );
};

export default ActionsBuilder;
